import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { AppIFrame } from "../components/AppIFrame";
import { getAppCfg } from "../core/app-cfg";
import { bufferUserData } from "../core/code/loader";
import { EDITOR_VIEW_NAME, LAUNCH_VIEW_NAME } from "../core/constants";
import { translate } from "../core/i18n";
import { isCloudProjectId } from "../core/project/projectid";
import { getDomainCfg, store } from "../core/state";
import { CLOUD_STATE } from "../features/cloudstorage/cloud";
import { getLauncherCode, getLauncherCodeAsync } from "../features/coderunner";
import { triggerIntegrationStyles } from "../features/integration";
import { loadSettingsFromLS, saveSettingsToLS } from "../features/localstorage/settings";
import { redirectToNewTab } from "../features/redirect";
import { LoginRegisterModal } from "../components/modals/LoginRegisterModal";

const VAIB_PATTERNS = /vaib\.js|VAIB\.init|VAIB\s*\(/i;

function detectVaibUsage(code: string | undefined): boolean {
  if (!code) return false;
  return VAIB_PATTERNS.test(code);
}

export function LaunchView() {
  const [launchState, setLaunchState] = store.useState(LAUNCH_VIEW_NAME);
  const [editorState] = store.useState(EDITOR_VIEW_NAME);
  const [cloudState] = store.useState(CLOUD_STATE);

  const title = `${getDomainCfg().product.name}-App`

  const localProject = !isCloudProjectId(getAppCfg().projectId);
  const hasLoadError = cloudState?.loadError === 'INVALID_LINK';
  const loadedData = localProject || cloudState?.loaded || launchState?.code?.length > 0;
  const showCodeFrame = localProject || launchState.showCodeFrame;

  const showSettings = launchState.showSettings;

  // Trigger integration styles after component renders
  useEffect(() => {
    triggerIntegrationStyles();
  }, [launchState, showCodeFrame, showSettings]);

  useEffect(() => {
    if (!launchState.data || !launchState.data.settings) {
      console.log('load settings', launchState.data)
      const loaded = loadSettingsFromLS();
      if (!loaded) {
        const currentLaunchState = store.getState(LAUNCH_VIEW_NAME).getValue();
        const newLaunchState = {
          ...currentLaunchState,
          data: {
            settings: {
              fullScreen: false,
              landscape: false,
              muteSound: false
            }
          }
        };
        console.log('set launch load settings', newLaunchState)
        setLaunchState(newLaunchState);
      }
    }
  }, [launchState.data, setLaunchState]);

  const fullScreenSetting = launchState.data?.settings?.fullScreen;
  const landscapeSetting = launchState.data?.settings?.landscape;
  const muteSoundSetting = launchState.data?.settings?.mute;

  const loadApp = (codeToLaunch) => {
    console.log("load app requested")

    setLaunchState({
      ...launchState,
      code: codeToLaunch,
      userAccepted: localProject ? true : false,
      timeStamp: Date.now()
    })
    console.log("set launch load app requested", launchState)
  }

  const showFrame = () => {
    console.log("set launch show frame requested")
    setLaunchState({
      ...launchState,
      showCodeFrame: true,
      userAccepted: localProject ? true : false
    })
    console.log("show frame requested", launchState)
  }

  const launchAppRequested = async () => {
    console.log("set launch launch app requested")

    bufferUserData();
    const codeToLaunch = await getLauncherCodeAsync(editorState);
    setLaunchState({
      ...launchState,
      showCodeFrame: true,
      userAccepted: true,
      code: codeToLaunch,
      timeStamp: Date.now()
    })
    console.log("launch app requested", launchState)
  }

  console.log("initial code loading? local, showframe", localProject, launchState)

  useEffect(() => {
    // Cloud-Projekte können asynchron geladen werden; vor `cloudState.loaded` nicht ausführen,
    // sonst kann `getMultiFileServicePayload` (V2) noch mit nicht initialisiertem VFS laufen.
    if (!hasLoadError && !launchState?.code && (localProject || cloudState?.loaded === true)) {
      getLauncherCodeAsync(editorState).then(codeToLaunch => {
        if (codeToLaunch) {
          const currentLaunchState = store.getState(LAUNCH_VIEW_NAME).getValue();
          if (localProject && currentLaunchState.showCodeFrame === false) {
            setLaunchState({
              ...currentLaunchState,
              showCodeFrame: true,
              userAccepted: true
            });
          } else if (!currentLaunchState?.code) { // Check again just in case
            setLaunchState({
              ...currentLaunchState,
              code: codeToLaunch,
              userAccepted: localProject ? true : false,
              timeStamp: Date.now()
            });
          }
        }
      });
    }
  }, [
    hasLoadError,
    launchState?.code,
    localProject,
    cloudState?.loaded,
    launchState.showCodeFrame,
    setLaunchState,
  ]);

  const showSettingsRequested = async () => {
    console.log('set launch show settings', launchState)
    setLaunchState({
      ...launchState,
      showSettings: true,
      timeStamp: Date.now()
    })
  }

  const closeSettingsRequested = () => {
    console.log('set launch close settings', launchState)
    setLaunchState({
      ...launchState,
      showSettings: false,
      timeStamp: Date.now()
    })
  }

  const switchFullScreen = () => {
    const newState = {
      ...launchState,
      data: {
        settings: {
          ...launchState.data.settings,
          fullScreen: !fullScreenSetting
        }
      },
      timeStamp: Date.now()
    };
    console.log('set launch switch fullscreen', launchState)
    setLaunchState(newState);
    saveSettingsToLS(newState.data.settings);
  }

  const switchLandscape = () => {
    const newState = {
      ...launchState,
      data: {
        settings: {
          ...launchState.data.settings,
          landscape: !landscapeSetting
        }
      },
      timeStamp: Date.now()
    };
    console.log('set launch switch landscape', launchState)
    setLaunchState(newState);
    saveSettingsToLS(newState.data.settings);
  }

  const switchMuteSound = () => {
    console.log('set launch mute sounds', launchState)
    setLaunchState({
      ...launchState,
      mute: !muteSoundSetting,
      timeStamp: Date.now()
    })

    const newState = {
      ...launchState,
      data: {
        settings: {
          ...launchState.data.settings,
          mute: !muteSoundSetting
        }
      }
    };
    console.log('set launch mute sounds2', launchState)
    setLaunchState(newState);
    saveSettingsToLS(newState.data.settings);
  }

  const navToHome = () => {
    redirectToNewTab(launchState?.cfg?.homeUrl);
  }
  const navToStart = () => {
    redirectToNewTab(launchState?.cfg?.startUrl);
  }

  const navToCta = () => {
    redirectToNewTab(launchState?.cfg?.callToActionUrl);
  }

  const navToSupport = () => {
    const param = getAppCfg().projectId;
    const mailParam = launchState?.cfg?.supportMail;
    const params = param + "&email=" + mailParam;
    redirectToNewTab(launchState?.cfg?.supportUrl + params);
  }

  const loading = (<>
    <div className="d-flex justify-content-center">
      <div className="spinner-grow text-warning" id="loading" role="status">
        <span className="sr-only">{translate('view-launch-loading')}</span>
        <p>{translate("launch-loading")}</p>
      </div>
    </div>

    <div id="project-launching" className="text-center">
      <p id="launch-status">{translate('view-launch-loading-app')}</p>
    </div>
  </>);

  const usesVaib = !localProject && detectVaibUsage(launchState.code);

  const vaibWarning = usesVaib ? (
    <div className="alert alert-warning text-center mt-2" role="alert">
      <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
      {translate('view-launch-vaib-warning') || 'Dieses Projekt nutzt KI-Funktionen (Kira), die deine Credits verbrauchen können.'}
    </div>
  ) : null;

  const start = (<>
    <div className="d-flex justify-content-center" id="project-launch">
      <button className="btn btn-success btn-xl" onClick={launchAppRequested}>{translate('view-launch-start-app')}</button>
    </div>
    {vaibWarning}
  </>);

  const errorView = (<>
    <div className="d-flex justify-content-center" id="project-launch-error">
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">{translate('view-launch-invalid-link') || 'Link ungültig'}</h4>
        <p>{translate('view-launch-invalid-link-message') || 'Für diesen Link sind keine öffentlichen Daten verfügbar.'}</p>
      </div>
    </div>
  </>);

  const landscapeElement = <div className="form-check">
    <input className="form-check-input" type="checkbox" defaultChecked={landscapeSetting} onChange={switchLandscape} id="landscapeSetting" />
    <label className="form-check-label" >
      {translate("view-launch-landscape")}
    </label>
  </div>;

  const settingsView = (<>
    <div className="row no-margin">
      <div className="col-sm no-padding justify-content-center">
        <p className="text-end"><button className="btn btn-outline-dark" onClick={closeSettingsRequested}>&times;</button></p>
        <div id="starter" className="text-center">
          <h1>{translate("view-launch-settings")}</h1>
        </div>
        <div className="form-check">
          <input className="form-check-input" type="checkbox" defaultChecked={fullScreenSetting} onChange={switchFullScreen} id="fullScreenSetting" />
          <label className="form-check-label" >
            {translate("view-launch-fullscreen")}
          </label>
        </div>
        <div className="form-check">
          <input className="form-check-input" type="checkbox" defaultChecked={muteSoundSetting} onChange={switchMuteSound} id="muteSetting" />
          <label className="form-check-label" >
            {translate("view-launch-audio")}
          </label>
        </div>
        <div className="mt-3">
          <button className="btn btn-outline-success m-2" onClick={navToHome}>{translate('view-launch-nav-to-home')}</button>
          <button className="btn btn-outline-warning m-2" onClick={navToStart}>{translate('view-launch-nav-to-start')}</button>
          <button className="btn btn-outline-danger m-2" onClick={navToSupport}>{translate('view-launch-nav-to-support')}</button>
        </div>
      </div>
    </div>
  </>);

  const startView = (<>
    <div className="row no-margin">
      <div className="col-sm no-padding">
        <div id="starter" className="text-center">
          <h1 id="title">{title}</h1>
        </div>
        {hasLoadError && errorView}
        {!hasLoadError && !loadedData && loading}
        {!hasLoadError && loadedData && start}
        <div className="alert alert-primary load-warning text-center" role="alert">
          <p className="small">{launchState?.cfg?.disclaimerText} </p>
          <p>{translate('view-launch-warning')}</p>
        </div>
      </div>
    </div>
  </>);

  const preLauncher = (<>
    <div className="container-fluid no-padding" id="start-screen">
      <p className="h3"><img src={getDomainCfg().product.logoUrl} className="imgcenter productLogo" max-height="57" alt="logo" /></p>
      <div className="rounded-lg shadow p-3 mb-5 bg-white border bordersize-15 m-5 p-5 mx-auto" style={{ maxWidth: 700 }}>
        {!showSettings && startView}
        {showSettings && settingsView}
      </div>
    </div>
  </>);
  const controls = (
    <div className="d-flex gap-2">
      <button className="btn btn-link p-0" onClick={launchAppRequested}>
        <FontAwesomeIcon icon="play" />
      </button>
      <button className="btn btn-link p-0" onClick={showSettingsRequested}>
        <FontAwesomeIcon icon="cog" />
      </button>
      <button className="btn btn-link p-0" onClick={navToSupport}>
        <FontAwesomeIcon icon="flag" />
      </button>
    </div>
  );

  const logoElm = (
    <>
      <img
        src={getDomainCfg().product.logoUrl}
        className="d-none d-md-inline-block"
        height="30"
        alt='Logo'
      />
      <img
        src={getDomainCfg().product.logoUrl}
        className="d-inline-block d-md-none"
        height="20"
        alt='Logo'
      />
    </>
  );

  const banner = (
    <div
      className="fixed-bottom bg-light py-2"
      style={{ zIndex: 1000 }}
    >
      <div className="container-fluid">
        <div className="row align-items-center g-2 flex-nowrap">
          <div className="col-auto text-truncate">
            <span className="d-none d-md-inline">{launchState?.cfg?.brandText}</span>
            {logoElm}
          </div>

          <div className="col text-center text-truncate">
            <span className="me-3 small d-none d-md-inline text-truncate">{launchState?.cfg?.disclaimerText}</span>
            <button
              className="btn btn-primary btn-sm"
              style={{
                boxShadow: '0 0 10px #007bff',
                animation: 'pulse 2s infinite',
                whiteSpace: 'nowrap'
              }}
              onClick={navToCta}
            >
              <FontAwesomeIcon icon="plus-circle" className="me-2" />
              <span className="d-none d-sm-inline">{launchState?.cfg?.callToActionText}</span>
              <span className="d-inline d-sm-none">Start</span>
            </button>
          </div>

          {launchState?.cfg?.bannerEnabled && (
            <div className="col-auto">
              {controls}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const codeFrame = (<>
    <div className="container-fluid no-padding black" id="apptablet">
      <div className="col-sm no-padding">
        {!launchState?.cfg?.bannerEnabled && (
          <div style={{
            padding: '10px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}>
            {controls}
          </div>
        )}
        <AppIFrame doc={launchState.code}
          timeStamp={launchState.timeStamp}
          classNames="ratio ratio-16x9 launch-view"
          fullScreen={fullScreenSetting}
          landscape={landscapeSetting}
          muteSound={muteSoundSetting}
          hasAppBorder={false} />
        {banner}
      </div>
    </div>
  </>);

  return (<>
    <LoginRegisterModal />
    {(!launchState.userAccepted || !showCodeFrame || showSettings) && preLauncher}
    {(launchState.userAccepted === true && showCodeFrame === true && !showSettings) && codeFrame}
  </>);
}
